import { UserAvatar } from "components/Atoms/Avatar";
import React from "react";
import { Message } from "components/Atoms/Message";
import { formatWrittenDateTimeUtc, newLineToBr } from "utilities";
import moment from "moment";

interface Props<T extends server.dto.Activity<any>> {
  button?: React.ReactNode
  error?: string | null
  activity: T
  cardColor?: "green" | "orange" | "purple" | "gray-dark" | "gray" | "blue";
  overrideTitle?: () => string
  overrideSubtitle?: () => string[]
}

const ActivityCard = <T extends server.dto.Activity<any>>(props: React.PropsWithChildren<Props<T>>) => {
  const { actor, actorName } = props.activity;
  const cardStyle = props.cardColor
    ? {
      borderLeft: `solid 6px var(--${props.cardColor})`,
      display: "block",
    } : {
      display: "block",
    };

  const timstampUtcMoment = moment.utc(props.activity.timestampUtc);

  const cardHover = [formatWrittenDateTimeUtc(timstampUtcMoment)];

  const title = props.overrideTitle ? props.overrideTitle() : props.activity.action.text;

  const subtitles = props.overrideSubtitle ? props.overrideSubtitle() : [`${timstampUtcMoment.fromNow()}`];

  if (props.activity.remoteAddress != null) cardHover.push(`IP: ${props.activity.remoteAddress}`);

  if (props.activity.application != null) cardHover.push(`Application: ${props.activity.application}`);

  return <div style={cardStyle} title={cardHover.join("\n")}>
    <div className="flex-column gap-small">
      <div className="flex gap">
        <div className="grow1 flex-column gap-small">
          <div className="flex">
            <div className="grow1">
              <>
                {(actorName != null) && <span className="s1_transparent60">{actorName} </span>}
                <strong>{title}</strong>
              </>
            </div>
          </div>

          {props.children}
        </div>

        {(actor) && <div>
          <UserAvatar user={actor} size="avatar-30" />
        </div>}
      </div>

      <div className="flex gap align-center">
        <div className="grow1 s1_small">
          <span className="s1_gray">{subtitles.join(" | ")}</span>
        </div>
        {props.button && <div>
          {props.button}
        </div>}
      </div>

      {props.error && <Message type="error">
        {props.error}
      </Message>}
    </div>
  </div>;
};

interface DefaultActivityContentProps {
  contentStyle?: React.CSSProperties
  activity: server.dto.Activity<any>
}

export const DefaultActivityContent = (props: DefaultActivityContentProps) => {
  if (!props.activity.content) return null;

  return <div className="flex">
    <div style={{ width: 3 }} className="gray-bg"></div>
    <div style={props.contentStyle} className="grow1 padding-small">
      {newLineToBr(props.activity.content)}
    </div>
  </div>;
}

interface ClickableActivityContentProps {
  value: string | null
  onClick: () => void
}

export const ClickableActivityContent = (props: ClickableActivityContentProps) => {
  if (!props.value) return null;

  return <div className="flex">
    <div style={{ width: 3 }} className="gray-bg"></div>
    <div className="grow1 padding-small">
      <a onClick={props.onClick}>{props.value}</a>
    </div>
  </div>
}

export default ActivityCard;